import React from "react";
import { STATUS_FLOW, EXCEPTION_STATUS } from "@/lib/api";
import { PUBLIC_TRACK } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { CheckCircle2, Circle, AlertTriangle, Truck } from "lucide-react";

function fmt(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

export const Timeline = ({ status, history = [] }) => {
  const isException = status === EXCEPTION_STATUS;
  const currentIndex = STATUS_FLOW.indexOf(status);
  const reached = (s) => history.some((h) => h.status === s);

  return (
    <div data-testid={PUBLIC_TRACK.timeline} className="space-y-6">
      {isException && (
        <div className="flex items-start gap-3 rounded-xl border border-rose-600/60 bg-rose-950/60 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-400" />
          <div>
            <p className="font-bold text-rose-200">Shipment Held / Exception</p>
            <p className="text-sm text-rose-300/80">
              This shipment is on hold. Contact support for remediation details.
            </p>
          </div>
        </div>
      )}

      {/* Horizontal stepper (desktop) */}
      <div className="hidden md:flex items-center">
        {STATUS_FLOW.map((step, i) => {
          const done = reached(step) || (!isException && i < currentIndex);
          const active = step === status;
          return (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center text-center w-28">
                <div
                  className={cn(
                    "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all",
                    done && "border-emerald-500 bg-emerald-500/15 text-emerald-400",
                    active && "border-amber-500 bg-amber-500/20 text-amber-300 shadow-[0_0_18px_rgba(245,158,11,0.45)]",
                    !done && !active && "border-slate-700 bg-slate-900 text-slate-600"
                  )}
                >
                  {active ? <Truck className="h-5 w-5" /> : done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-4 w-4" />}
                </div>
                <p className={cn("mt-2 text-xs font-semibold", (done || active) ? "text-slate-200" : "text-slate-500")}>{step}</p>
              </div>
              {i < STATUS_FLOW.length - 1 && (
                <div className={cn("h-0.5 flex-1 -mt-6", done ? "bg-emerald-500/60" : "bg-slate-700")} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Vertical event log */}
      <div className="relative space-y-4 md:border-t md:border-white/10 md:pt-6">
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">Event History</p>
        <ol className="relative border-l border-white/10 pl-6">
          {[...history].reverse().map((h, idx) => (
            <li key={`${h.status}-${h.timestamp}-${idx}`} className="mb-5 last:mb-0">
              <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-amber-500 ring-4 ring-slate-950" />
              <p className="font-semibold text-slate-100">{h.status}</p>
              {h.note && <p className="text-sm text-slate-400">{h.note}</p>}
              <p className="text-xs font-mono text-slate-500">{fmt(h.timestamp)}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
